/* eslint-disable no-unused-vars */
import { useMemo } from 'react';
import { quickFilterViewsLabels } from 'src/redux/slices/taptap-customer';
import {
  departmentTaptapPermissionState,
  quickFilterViewsChoicesV2,
  superiorTapTapRoles
} from 'src/redux/slices/taptap-customer/constants/taptap-flow-process';
import {
  resolveCurrentScenario,
  resolveEffectiveStatusFlow,
  resolveNextAction,
  resolveNextStatus,
  resolveTicketScenario
} from 'src/redux/slices/taptap-customer/flow/taptap-flow-engine';
import { TapTapCustomerData } from 'src/redux/slices/taptap-customer/types';
import { usePermissions } from '../use-permissions';
import useUserInfo from '../user/use-user-info';

type UseTaptapFlowOptions = {
  hasSystemGeneratedTicket?: boolean;
  paymentTotal?: number;
  isReadOnly?: boolean;
  isStatusLoading?: boolean;
};

export const useTaptapFlow = (
  customerTaptap?: TapTapCustomerData,
  options?: UseTaptapFlowOptions
) => {
  const { userDetails } = useUserInfo();
  const { userPermissionsStringArray } = usePermissions();
  const hasSystemGeneratedTicket = options?.hasSystemGeneratedTicket;
  const paymentTotal = options?.paymentTotal;
  const isReadOnly = options?.isReadOnly;
  const isStatusLoading = options?.isStatusLoading;

  const roles = useMemo(
    () => userDetails?.all_roles?.map((r) => r.name) || [],
    [userDetails]
  );

  const hasAllTaptapDashboardPermission = useMemo(
    () => userPermissionsStringArray.includes('can_view_all_taptap_dashboard'),
    [userPermissionsStringArray]
  );

  /** DEPARTMENT */
  const departments = useMemo(() => {
    if (hasAllTaptapDashboardPermission) {
      return departmentTaptapPermissionState.filter(
        (dept) => dept.department === 'ALL'
      );
    }

    const permissionSet = new Set(userPermissionsStringArray);

    return departmentTaptapPermissionState.filter((dept) =>
      dept.permissions.some((permission) => permissionSet.has(permission))
    );
  }, [hasAllTaptapDashboardPermission, userPermissionsStringArray]);

  const allowedStatuses = useMemo(() => {
    const merged = departments.flatMap((d) => d.status_option);
    return Array.from(new Set(merged));
  }, [departments]);

  /** SOURCE */
  const ticketScenario = useMemo(() => {
    return resolveTicketScenario(customerTaptap);
  }, [customerTaptap]);

  /** SCENARIO MATCH */
  const currentScenario = useMemo(() => {
    return resolveCurrentScenario(ticketScenario);
  }, [ticketScenario]);

  const effectiveStatusFlow = useMemo(() => {
    return resolveEffectiveStatusFlow({
      customer: customerTaptap,
      currentScenario,
      options: { hasSystemGeneratedTicket, paymentTotal }
    });
  }, [customerTaptap, currentScenario, hasSystemGeneratedTicket, paymentTotal]);

  /** FLOW */
  const currentStatusIndex = useMemo(() => {
    return effectiveStatusFlow.indexOf(customerTaptap?.status || '');
  }, [customerTaptap, effectiveStatusFlow]);

  /**================ROLE BASED VIEWS SELECTION====================== */
  const roleBasedViewLabel = useMemo(() => {
    if (hasAllTaptapDashboardPermission) {
      return quickFilterViewsLabels.ALL;
    }

    const permissionBasedView = quickFilterViewsChoicesV2.find((view) => {
      if (!view.permission) {
        return false;
      }

      return userPermissionsStringArray.includes(view.permission);
    });

    if (permissionBasedView) {
      return permissionBasedView.label;
    }

    if (roles.some((role) => superiorTapTapRoles.includes(role || ''))) {
      return quickFilterViewsLabels.ALL;
    }

    return undefined;
  }, [hasAllTaptapDashboardPermission, roles, userPermissionsStringArray]);

  const isPaymentVerificationView =
    roleBasedViewLabel === quickFilterViewsLabels.PAYMENT_VERIFICATION;

  const nextStatus = useMemo(() => {
    return resolveNextStatus({
      customer: customerTaptap,
      effectiveStatusFlow,
      hasSystemGeneratedTicket,
      isPaymentVerificationView,
      paymentTotal
    });
  }, [
    customerTaptap,
    effectiveStatusFlow,
    hasSystemGeneratedTicket,
    isPaymentVerificationView,
    paymentTotal
  ]);

  /** PERMISSION */
  const canProceed = useMemo(() => {
    return Boolean(nextStatus && allowedStatuses.includes(nextStatus));
  }, [allowedStatuses, nextStatus]);

  const nextAction = useMemo(() => {
    return resolveNextAction({
      customer: customerTaptap,
      nextStatus,
      canProceed,
      isReadOnly,
      isStatusLoading,
      paymentTotal
    });
  }, [
    canProceed,
    customerTaptap,
    isReadOnly,
    isStatusLoading,
    nextStatus,
    paymentTotal
  ]);

  return {
    roles,
    departments,
    allowedStatuses,
    ticketScenario,
    currentScenario,
    currentStatusIndex,
    effectiveStatusFlow,
    nextStatus,
    nextAction,
    canProceed,
    roleBasedViewLabel
  };
};
